import { waitForTransactionReceipt } from '@/utils/wagmi/waitForTransactionReceipt'
import { useWriteFactoryContract } from '@/hooks/survey-factory-contract/useWriteSurveyFactoryContract'
import { deepDiff } from '@/utils/deep-diff'
import { encodeFunctionData, type Hex, parseEther } from 'viem'
import { abi } from '@/abi/Survey4'

type SurveyParams = {
	question: string
	answers: string[]
	votingCap: number
	startTime: number
	endTime: number
}

export function useEditPoll() {
	const writeSurveyFactory = useWriteFactoryContract()

	const editPoll = async ({
		currentData,
		newData,
		address
	}: {
		currentData: SurveyParams
		newData: SurveyParams
		address: Hex
	}) => {
		const { changes } = deepDiff({
			source: currentData,
			target: newData,
			deepArray: false
		})

		const { votingCap, startTime, endTime, answers } = changes

		const fns = [
			...(typeof votingCap?.to === 'number' ? [encodeSetVotingCap(votingCap.to)] : []),
			...(Array.isArray(answers?.to) ? [encodeSetAnswerIds(answers.to)] : []),
			...(typeof startTime?.to === 'number' ? [encodeSetStartDate(startTime.to)] : []),
			...(typeof endTime?.to === 'number' ? [encodeSetEndDate(endTime.to)] : [])
		]

		const res = await Promise.all([
			...fns.map(fn => writeSurveyFactory({ functionName: 'executeSurveyUpdate', args: [address, fn] }))
		])

		await Promise.all(res.map(hash => waitForTransactionReceipt({ hash })))
	}

	return { editPoll }
}

function encodeSetVotingCap(votingCap: number) {
	const votingCapETH = parseEther(votingCap.toString())
	return encodeFunctionData({
		abi,
		functionName: 'setVotingCap',
		args: [votingCapETH]
	})
}

function encodeSetAnswerIds(answers: string[]) {
	return encodeFunctionData({
		abi,
		functionName: 'setAnswerIds',
		args: [answers]
	})
}

function encodeSetStartDate(date: number) {
	return encodeFunctionData({
		abi,
		functionName: 'setStartDate',
		args: [BigInt(date / 1000)]
	})
}

function encodeSetEndDate(date: number) {
	return encodeFunctionData({
		abi,
		functionName: 'setEndDate',
		args: [BigInt(date / 1000)]
	})
}
